import { useEffect } from 'react';
import { InputField } from 'lib/iterable-hook-form';
import { ConversionEventSelect } from 'modules/campaigns/lib/components/ConversionSelect';
import { CampaignIdBadge } from 'modules/campaigns/lib/components/CampaignIdBadge';
import { AttributionMode } from 'modules/domains/campaign';
import { z } from 'zod';
import { useFormContext, useWatch } from 'react-hook-form';
import { InfoBanner } from 'ui/banner';

import { BackendJourneyConversionEvent } from 'modules/workflows/types/workflow';
import {
  StyledAttributionWrapper,
  InputWrapper,
  StyledConversionWrapper,
} from './JourneyConversions.styled';

import {
  ATTRIBUTION_MODE_KEY,
  ATTRIBUTION_PERIOD_HOURS_KEY,
  CONVERSION_ATTRIBUTION_ERROR_MESSAGE,
  DEFAULT_CONVERSIONS_PERIOD_HOUR,
  MAX_CONVERSIONS_PERIOD_HOUR,
  MIN_CONVERSIONS_PERIOD_HOUR,
  ATTRIBUTION_MODE,
  ATTRIBUTION_PERIOD_HOURS,
  ATTRIBUTION_VALIDATION_KEY,
} from './JourneyConversions.constants';

import { AttributionRow } from './AttributionRow';

export type JourneyConversion = {
  event: string;
  mode: AttributionMode;
  periodHours: string;
};

export const conversionSchema = z.object({
  conversions: z
    .object({
      event: z.string().optional(),
      mode: z.nativeEnum(AttributionMode).optional(),
      periodHours: z.string().optional(),
    })
    .optional()
    .superRefine((data, ctx) => {
      if (data?.periodHours === '')
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: CONVERSION_ATTRIBUTION_ERROR_MESSAGE,
        });

      // we should look at refactoring these into functions that take a failure call back so we can write tests.
      const isLastMessagedAttributionMode =
        data?.[ATTRIBUTION_MODE] === AttributionMode.LastMessaged;
      const periodHours = parseInt(data?.[ATTRIBUTION_PERIOD_HOURS] ?? '', 10); // returns number or NaN

      if (
        isLastMessagedAttributionMode &&
        (periodHours < MIN_CONVERSIONS_PERIOD_HOUR || periodHours > MAX_CONVERSIONS_PERIOD_HOUR)
      ) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: CONVERSION_ATTRIBUTION_ERROR_MESSAGE,
        });
      }
    }),
});

export const defaultConversionValues: JourneyConversion = {
  event: '',
  mode: AttributionMode.LastMessaged,
  periodHours: DEFAULT_CONVERSIONS_PERIOD_HOUR.toString(),
};

export const getDefaultConversions = (
  currentConversion?: BackendJourneyConversionEvent,
): JourneyConversion => {
  return {
    event: currentConversion?.eventName ?? defaultConversionValues.event,
    mode:
      ((currentConversion?.attributionMode as AttributionMode) === AttributionMode.AttributionPeriod
        ? AttributionMode.LastMessaged
        : (currentConversion?.attributionMode as AttributionMode)) ?? defaultConversionValues.mode,
    periodHours:
      currentConversion?.attributionPeriodHours?.toLocaleString() ??
      defaultConversionValues.periodHours,
  };
};

export const JourneyConversions = ({
  isDisabled = false,
  showInfo = false,
  runValidation = false,
}: {
  runValidation?: boolean;
  isDisabled?: boolean;
  showInfo?: boolean;
}) => {
  const conversions = useWatch({ name: ATTRIBUTION_VALIDATION_KEY });

  const {
    watch,
    setValue,
    trigger,
    formState: { errors, dirtyFields },
  } = useFormContext();

  useEffect(() => {
    watch((_, { name }) => {
      if (name === ATTRIBUTION_MODE_KEY) {
        setValue(ATTRIBUTION_PERIOD_HOURS_KEY, DEFAULT_CONVERSIONS_PERIOD_HOUR.toString());
      }
      if (runValidation) {
        trigger(ATTRIBUTION_VALIDATION_KEY);
      }
    });
  }, [watch, trigger, setValue]);

  const { event, mode } = conversions ?? {};

  const conversionFieldsChanged = Object.keys(dirtyFields?.conversions ?? {}).length > 0;

  return (
    <StyledConversionWrapper>
      <ConversionEventSelect id="journey-conversions" isDisabled={isDisabled} />
      {event && (
        <>
          <StyledAttributionWrapper>
            <AttributionRow
              name={ATTRIBUTION_MODE_KEY}
              isDisabled={isDisabled}
              attributionMode={AttributionMode.LastMessaged}
              text="Last touch attribution within"
              error={errors?.conversions?.message && CONVERSION_ATTRIBUTION_ERROR_MESSAGE}
            >
              <InputWrapper>
                <InputField
                  isDisabled={mode !== AttributionMode.LastMessaged || isDisabled}
                  name={ATTRIBUTION_PERIOD_HOURS_KEY}
                  type="number"
                  placeholder={DEFAULT_CONVERSIONS_PERIOD_HOUR.toString()}
                  Suffix="hours"
                  hasError={!!errors?.conversions?.message}
                />
              </InputWrapper>
            </AttributionRow>

            <AttributionRow
              name={ATTRIBUTION_MODE_KEY}
              attributionMode={AttributionMode.ExplicitlySet}
              isDisabled={isDisabled}
              text={
                <div>
                  {`Attribute only conversion events with explicitly set `}
                  <CampaignIdBadge /> field
                </div>
              }
            />
          </StyledAttributionWrapper>
          {showInfo && conversionFieldsChanged && (
            <InfoBanner>
              The conversion event and attribution method you select for this journey&apos;s
              conversion goal will be added to all campaigns associated with this journey.
              Conversion goal monitoring will start once the journey is updated.
            </InfoBanner>
          )}
        </>
      )}
    </StyledConversionWrapper>
  );
};
